import React, { useState, useContext } from 'react';
import { FirebaseContext } from '../App'; // Import FirebaseContext
import { addVideo } from '../utils/firestoreService'; // Import Firestore service
import './UploadForm.css';

function UploadForm({ userId, uploaderXUsername }) {
    // Access db from FirebaseContext
    const { db } = useContext(FirebaseContext);

    const [title, setTitle] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [category, setCategory] = useState('CODM'); // Default category updated
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Updated list of available categories
    const categories = [
        'CODM',
        'PUBG',
        'Crisis of Trust',
        'Others'
    ];

    // Updated URL validation regex to handle query parameters after file extensions
    // It now matches:
    // 1. Specific video/image hosting domains (YouTube, Vimeo, Twitch, etc.)
    // 2. Direct links to video/image files (e.g., .mp4, .gif) that might have query parameters
    const urlRegex = /^(https?:\/\/(?:www\.|m\.)?(?:youtube\.com|youtu\.be|vimeo\.com|twitch\.tv|clips\.twitch\.tv|streamable\.com|gfycat\.com|imgur\.com|media\.giphy\.com|cdn\.discordapp\.com|i\.redd\.it|i\.imgur\.com|video\.twimg\.com)\S*)|(https?:\/\/\S+\.(?:mp4|webm|ogg|mov|avi|flv|wmv|mkv|gif|jpg|jpeg|png|webp)(?:\?|#)\S*)?$/i;


    // Handles the submission of the video upload form
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setIsLoading(true);

        // Basic validation
        if (!title.trim() || !videoUrl.trim() || !category.trim()) {
            setError('Please fill in all required fields (Title, Video URL, Category).');
            setIsLoading(false);
            return;
        }

        // Validate URL format
        if (!urlRegex.test(videoUrl.trim())) {
            setError('Please enter a valid video URL (YouTube, Vimeo, Twitch, X direct video link, or direct video/image link).');
            setIsLoading(false);
            return;
        }

        if (!db || !userId || !uploaderXUsername) {
            setError("Firebase connection or user data not ready. Please try again.");
            setIsLoading(false);
            return;
        }

        const videoData = {
            title: title.trim(),
            videoUrl: videoUrl.trim(),
            category: category,
            description: description.trim(),
            // uploaderId and uploaderXUsername are added by addVideo function
        };

        try {
            const success = await addVideo(db, userId, uploaderXUsername, videoData);
            if (success) {
                setMessage('Video uploaded successfully!');
                // Clear form fields
                setTitle('');
                setVideoUrl('');
                setCategory('CODM'); // Reset to default CODM
                setDescription('');
            } else {
                setError('Failed to upload video. Please try again.');
            }
        } catch (err) {
            console.error("Error uploading video:", err);
            setError(`Error uploading video: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="upload-form-container">
            <form onSubmit={handleSubmit} className="upload-form">
                <div className="form-group">
                    <label htmlFor="title">Video Title:</label>
                    <input
                        type="text"
                        id="title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g., My Best Headshots Montage"
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="videoUrl">Video URL:</label>
                    <input
                        type="url"
                        id="videoUrl"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="e.g., https://www.youtube.com/watch?v=dQw4w9WgXcQ or https://video.twimg.com/..."
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="category">Category:</label>
                    <select
                        id="category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        required
                    >
                        {categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label htmlFor="description">Description (Optional):</label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Tell us about your game edit..."
                        rows="3"
                    ></textarea>
                </div>
                {error && <p className="upload-error">{error}</p>}
                {message && <p className="upload-message">{message}</p>}
                <button type="submit" className="upload-button" disabled={isLoading}>
                    {isLoading ? 'Uploading...' : 'Upload Video'}
                </button>
            </form>
        </div>
    );
}

export default UploadForm;
